import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { Drawer, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { Box, Button, Grid, Stack } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import InsightsIcon from '@mui/icons-material/Insights';
import ClickAwayListener from '@mui/base/ClickAwayListener';

import logo from '../../assets/logoMobile.jpg';
import logoMini from '../../assets/logoMobileSidebarCollapsed.jpg';
import { SidebarProps } from 'src/interfaces/iproptypes';
import { SidebarItemType } from 'src/interfaces/idashboard';
import useStyles from './styles';
import SidebarItem from './SidebarItem';
import CurrencyChangeMenu from '../CurrencyChangeMenu';
import { useOpenOverviewOnboardingPage } from 'src/utils/hooks';
import { COLOR_PRIMARY, sidebarClosedWidth, sidebarWidth } from 'src/const';
import { COLOR_GREY_600, COLOR_PRIMARY_2 } from 'src/colors';
import { useHistory } from 'react-router-dom';
const sideBarIconStyle = { fontSize: 25, color: '#000', padding: '2 0' };

// main component

const sidebarItems: SidebarItemType[] = [
  {
    icon: <InsightsIcon style={sideBarIconStyle} />,
    name: 'Overview',
    isPage: false,
    stateName: 'main',
    listIndex: 0,
    children: [],
  },
  {
    icon: <CalculateIcon style={sideBarIconStyle} />,
    name: 'Calculator',
    isPage: false,
    stateName: 'estimator',
    listIndex: 1,
    children: [],
  },
];

const Sidebar = ({
  parent,
  handleSidebarClick,
  sidebarOpen,
  setSidebarOpen,
  matches,
  selectedSidebarIndex,
  handleSidebarListItemClick,
  currency,
  setCurrency,
  isUserVerified,
  setIsEmailVerifyModalOpen,
  dashboardState,
  user,
  isLoggedIn,
  showHeaderOrBottomBarOnMobileView
}: SidebarProps) => {
  const classes = useStyles();
  const [sideBarReadyToCollapse, setSideBarReadyToCollapse] = useState(false);

  const history = useHistory();

  useEffect(() => {
    if (window.innerWidth < 1050) {
      setSidebarOpen(false);
    }
  }, [matches]);

  useEffect(() => {
    setTimeout(() => {
      setSideBarReadyToCollapse(sidebarOpen);
    }, 500);
  }, [sidebarOpen]);

  const isTopBannerOpen = useSelector((state: any) => state.dashboard.isTopBannerOpen);
  const openOverviewOnboardingPage = useOpenOverviewOnboardingPage();

  const handleClickAway = () => {
    if (matches && sideBarReadyToCollapse) {
      setSidebarOpen(false);
    }
  };

  const EmailVerificationComponent = (
    <ListItem
      style={{
        cursor: 'pointer',
        pointerEvents: 'auto',
        paddingLeft: '0',
        ...(matches && { display: 'flex', justifyContent: 'center', alignItems: 'center' }),
      }}
      onClick={() => setIsEmailVerifyModalOpen(true)}
    >
      <ListItemIcon>
        <Stack
          direction={'column'}
          justifyContent="flex-start"
          alignItems="center"
          spacing={0}
          sx={{
            background: 'transparent',
            padding: '2.5px 4px',
            paddingRight: '12px',
            borderRadius: 4,
            maxWidth: matches ? sidebarWidth : sidebarClosedWidth,
          }}
        >
          <MarkEmailUnreadIcon
            style={{
              color: '#000',
              animation: 'shake-up-down 3.5s 5s ease-in-out infinite',
              paddingRight: '5px',
            }}
          />
          <ListItemText
            classes={{
              primary: classes.sidebarText,
            }}
            primary="Verify Email"
            style={{ maxWidth: sidebarClosedWidth }}
          />
        </Stack>
      </ListItemIcon>
    </ListItem>
  );

  const isEmailVerificationOptionVisible = isLoggedIn && isUserVerified === false && !user.loading;

  if (!showHeaderOrBottomBarOnMobileView) return <></>;

  // Mobile bottom bar
  if (matches) {
    return (
      <Grid
        container
        justifyContent={'center'}
        alignItems={'center'}
        sx={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          background: '#fff',
          padding: '10px auto',
          boxShadow: '0 -4px 12px 0px #0003',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          zIndex: 1200,
          // gap: '20px',
        }}
      >
        {sidebarItems.map(sideBarItem => {
          return (
            <Grid
              item
              xs={isEmailVerificationOptionVisible ? 4 : 6}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <Button
                onClick={() => {
                  if ((sideBarItem.name === 'Calculator')) {
                    history.push('/airbnb-calculator');
                  }
                  handleSidebarListItemClick(sideBarItem.listIndex);
                  handleSidebarClick && handleSidebarClick(sideBarItem.stateName);
                }}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  color:
                    sideBarItem.listIndex === selectedSidebarIndex
                      ? COLOR_PRIMARY_2
                      : COLOR_GREY_600,
                  marginTop: '10px',
                  marginBottom: '10px',
                  padding: '5px 10px',
                  '& .MuiSvgIcon-root': {
                    color:
                      (sideBarItem.listIndex === selectedSidebarIndex
                        ? COLOR_PRIMARY_2
                        : COLOR_GREY_600) + '!important',
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: 0,
                    marginRight: 0,
                  },
                }}
                startIcon={sideBarItem.icon}
              >
                {sideBarItem.name}
              </Button>
            </Grid>
          );
        })}

        {isEmailVerificationOptionVisible && (
          <Grid item xs={4}>
            {EmailVerificationComponent}
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.paperDrawer, {
            [classes.drawerOpen]: sidebarOpen && matches,
            [classes.drawerClose]: !sidebarOpen || (sidebarOpen && !matches),
            [classes.bannerOpen]: isTopBannerOpen,
          }),
        }}
        className="sidebar-scroll"
      >
        <Button
          className={classes.logoButton}
          onClick={() => {
            openOverviewOnboardingPage();
          }}
        >
          {matches && sidebarOpen ? (
            <img src={logo} className={classes.logo} />
          ) : (
            <img src={logoMini} className={classes.logoMini} />
          )}
        </Button>

        {/* Dashboard */}
        <div>
          {sidebarItems.map((data, index) => {
            return (
              <SidebarItem
                item={data}
                isSubGroup={false}
                handleSidebarClick={handleSidebarClick}
                handleSidebarListItemClick={handleSidebarListItemClick}
                matches={matches}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                index={index}
                selectedSidebarIndex={selectedSidebarIndex}
                user={user}
              />
            );
          })}

          {/* Email verification button */}
          {isEmailVerificationOptionVisible && (
            <div
              style={{
                backgroundColor: 'transparent',
                margin: '10px',
                borderRadius: 10,
                overflow: 'hidden',
              }}
            >
              {EmailVerificationComponent}
            </div>
          )}
        </div>

        {matches && currency && (
          <List
            disablePadding={true}
            style={{
              position: 'relative',
              cursor: 'pointer',
              width: '100%',
              marginBottom: '20px',
            }}
          >
            <ListItem style={{ paddingLeft: 0 }}>
              <CurrencyChangeMenu
                currency={currency}
                setCurrency={setCurrency}
                fullWidth={true}
                darkTheme={true}
              />
            </ListItem>
          </List>
        )}
      </Drawer>
    </ClickAwayListener>
  );
};

export default Sidebar;
